import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Table,
  Statistic,
  DatePicker,
  Button,
  Space,
  Spin,
  Empty,
  Alert,
  Dropdown,
  Tag,
  message
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  BarChartOutlined,
  PieChartOutlined,
  PictureOutlined,
  DownOutlined,
  ExportOutlined
} from '@ant-design/icons';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import { getCurrentBeijingTime, TIME_FORMATS } from '../../utils/timeUtils';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { getTrainingInfoList } from '../../services/api';
import { TrainingInfoCollection, TrainingInfoQueryParams } from '../../types';

const { Title } = Typography;

interface AnalysisData {
  trainingModeStats: {
    mode: string;
    people: number;
    classes: number;
  }[];
  trainingTypeStats: {
    type: string;
    people: number;
    classes: number;
  }[];
  regionStats: {
    region: string;
    total: number;
    self: number;
  }[];
  selfTrainingRegionStats: {
    region: string;
    people: number;
    classes: number;
  }[];
  selfTrainingTypePlanStats: {
    type: string;
    plannedClasses: number;
  }[];
  overview: {
    totalPeople: number;
    totalClasses: number;
    selfTrainingPeople: number;
    cooperativeTrainingPeople: number;
  };
}

const TrainingDataAnalysis: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('2025-09'); // 暂时设置为数据库中有数据的月份
  const [trainingData, setTrainingData] = useState<TrainingInfoCollection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isShowingHistoricalData, setIsShowingHistoricalData] = useState(false);
  const [analysisData, setAnalysisData] = useState<AnalysisData>({
    trainingModeStats: [],
    trainingTypeStats: [],
    regionStats: [],
    selfTrainingRegionStats: [],
    selfTrainingTypePlanStats: [],
    overview: {
      totalPeople: 0,
      totalClasses: 0,
      selfTrainingPeople: 0,
      cooperativeTrainingPeople: 0,
    }
  });

  // 数据处理函数
  const processTrainingData = (data: TrainingInfoCollection[]): AnalysisData => {
    // 按培训模式统计
    const modeStats = new Map<string, { people: number; classes: number }>();

    // 按业务类型统计（可以用作培训类别）
    const typeStats = new Map<string, { people: number; classes: number }>();

    // 按地区统计
    const regionStats = new Map<string, { total: number; self: number }>();

    // 按地区统计（仅自主培训）
    const selfTrainingRegionStats = new Map<string, { people: number; classes: number }>();

    // 按培训类别统计自主培训计划开班数
    const selfTrainingTypePlanStats = new Map<string, { plannedClasses: number }>();

    let totalPeople = 0;
    let totalClasses = 0;
    let selfTrainingPeople = 0;
    let cooperativeTrainingPeople = 0;

    data.forEach(record => {
      const people = record.current_month_students || 0;
      const classes = record.current_month_classes || 0;

      totalPeople += people;
      totalClasses += classes;

      // 处理培训模式
      record.training_modes.forEach(mode => {
        const modeKey = mode;
        const existing = modeStats.get(modeKey) || { people: 0, classes: 0 };
        modeStats.set(modeKey, {
          people: existing.people + people,
          classes: existing.classes + classes
        });

        // 区分自主培训和合作培训
        if (mode.includes('自主')) {
          selfTrainingPeople += people;
        } else if (mode.includes('合作')) {
          cooperativeTrainingPeople += people;
        }
      });

      // 处理业务类型（作为培训类别）
      const typeKey = record.business_type_name || '其他';
      const existingType = typeStats.get(typeKey) || { people: 0, classes: 0 };
      typeStats.set(typeKey, {
        people: existingType.people + people,
        classes: existingType.classes + classes
      });

      // 处理地区
      const regionKey = record.region_name || '未知地区';
      const existingRegion = regionStats.get(regionKey) || { total: 0, self: 0 };
      const selfCount = record.training_modes.some(mode => mode.includes('自主')) ? people : 0;
      regionStats.set(regionKey, {
        total: existingRegion.total + people,
        self: existingRegion.self + selfCount
      });

      // 处理自主培训地区统计
      const hasSelfTraining = record.training_modes.some(mode => mode.includes('自主'));
      if (hasSelfTraining) {
        const existingSelfRegion = selfTrainingRegionStats.get(regionKey) || { people: 0, classes: 0 };
        selfTrainingRegionStats.set(regionKey, {
          people: existingSelfRegion.people + people,
          classes: existingSelfRegion.classes + classes
        });
      }

      // 处理自主培训培训类别计划开班数统计
      if (hasSelfTraining) {
        const plannedClasses = record.next_month_classes || 0;
        if (plannedClasses > 0) {
          const existingTypePlan = selfTrainingTypePlanStats.get(typeKey) || { plannedClasses: 0 };
          selfTrainingTypePlanStats.set(typeKey, {
            plannedClasses: existingTypePlan.plannedClasses + plannedClasses
          });
        }
      }
    });

    return {
      trainingModeStats: Array.from(modeStats.entries()).map(([mode, stats]) => ({
        mode,
        people: stats.people,
        classes: stats.classes
      })),
      trainingTypeStats: Array.from(typeStats.entries()).map(([type, stats]) => ({
        type,
        people: stats.people,
        classes: stats.classes
      })),
      regionStats: Array.from(regionStats.entries())
        .map(([region, stats]) => ({
          region,
          total: stats.total,
          self: stats.self
        }))
        .sort((a, b) => b.total - a.total), // 按总人数降序排列
      selfTrainingRegionStats: Array.from(selfTrainingRegionStats.entries())
        .map(([region, stats]) => ({
          region,
          people: stats.people,
          classes: stats.classes
        }))
        .sort((a, b) => b.people - a.people), // 按自主培训人数降序排列
      selfTrainingTypePlanStats: Array.from(selfTrainingTypePlanStats.entries())
        .map(([type, stats]) => ({
          type,
          plannedClasses: stats.plannedClasses
        }))
        .sort((a, b) => b.plannedClasses - a.plannedClasses), // 按计划开班数降序排列
      overview: {
        totalPeople,
        totalClasses,
        selfTrainingPeople,
        cooperativeTrainingPeople
      }
    };
  };

  // 导出数据分析报告
  const exportAnalysisReport = () => {
    if (!hasData) {
      message.warning('暂无数据可导出');
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      // 设置工作簿属性
      workbook.Props = {
        Title: `培训数据分析报告-${selectedMonth}`,
        Subject: "培训数据统计分析",
        Author: "培训费用标准化检查与预警平台",
        CreatedDate: new Date()
      };

      // 1. 概览统计表
      const overviewData = [
        ['统计项目', '数值'],
        ['总培训人数', `${analysisData.overview.totalPeople}人`],
        ['总班级数', `${analysisData.overview.totalClasses}个`],
        ['自主培训人数', `${analysisData.overview.selfTrainingPeople}人`],
        ['合作培训人数', `${analysisData.overview.cooperativeTrainingPeople}人`],
      ];
      const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
      XLSX.utils.book_append_sheet(workbook, overviewSheet, '概览统计');

      // 2. 培训模式统计表
      const modeStatsData = [
        ['培训模式', '培训人数', '班级数', '占比(%)'],
        ...analysisData.trainingModeStats.map(item => [
          item.mode,
          item.people,
          item.classes,
          ((item.people / analysisData.overview.totalPeople) * 100).toFixed(2)
        ])
      ];
      const modeStatsSheet = XLSX.utils.aoa_to_sheet(modeStatsData);

      // 设置列宽
      modeStatsSheet['!cols'] = [
        { width: 15 }, // 培训模式
        { width: 12 }, // 培训人数
        { width: 10 }, // 班级数
        { width: 12 }  // 占比
      ];

      XLSX.utils.book_append_sheet(workbook, modeStatsSheet, '培训模式统计');

      // 3. 培训类别统计表
      const totalTypesPeople = analysisData.trainingTypeStats.reduce((sum, item) => sum + item.people, 0);
      const typeStatsData = [
        ['培训类别', '培训人数', '班级数', '占比(%)'],
        ...analysisData.trainingTypeStats.map(item => [
          item.type,
          item.people,
          item.classes,
          totalTypesPeople > 0 ? ((item.people / totalTypesPeople) * 100).toFixed(2) : '0.00'
        ])
      ];
      const typeStatsSheet = XLSX.utils.aoa_to_sheet(typeStatsData);
      typeStatsSheet['!cols'] = [{ width: 15 }, { width: 12 }, { width: 10 }, { width: 12 }];
      XLSX.utils.book_append_sheet(workbook, typeStatsSheet, '培训类别统计');

      // 4. 地区培训排名
      const regionRankData = [
        ['地区', '总培训人数', '自主培训人数'],
        ...analysisData.regionStats.map(item => [
          item.region,
          item.total,
          item.self
        ])
      ];
      const regionRankSheet = XLSX.utils.aoa_to_sheet(regionRankData);
      XLSX.utils.book_append_sheet(workbook, regionRankSheet, '地区培训排名');

      // 5. 自主培训地区排名
      const selfRegionRankData = [
        ['地区', '自主培训人数', '自主培训班级数'],
        ...analysisData.selfTrainingRegionStats.map(item => [
          item.region,
          item.people,
          item.classes
        ])
      ];
      const selfRegionRankSheet = XLSX.utils.aoa_to_sheet(selfRegionRankData);
      XLSX.utils.book_append_sheet(workbook, selfRegionRankSheet, '自主培训地区排名');

      // 6. 原始数据明细
      const rawDataHeaders = [
        '序号', '地区', '业务类型', '培训模式', '当月学员数', '当月班级数',
        '下月计划班级数', '下月计划学员数', '联系人', '平均日薪', '收集月份'
      ];
      const rawData = [
        rawDataHeaders,
        ...trainingData.map((item, index) => [
          index + 1,
          item.region_name || '',
          item.business_type_name || '',
          Array.isArray(item.training_modes) ? item.training_modes.join(', ') : item.training_modes,
          item.current_month_students || 0,
          item.current_month_classes || 0,
          item.next_month_classes || 0,
          item.next_month_students || 0,
          item.contact || '',
          item.average_daily_salary || 0,
          item.collection_month || ''
        ])
      ];
      const rawDataSheet = XLSX.utils.aoa_to_sheet(rawData);
      rawDataSheet['!cols'] = [
        { width: 6 }, { width: 12 }, { width: 15 }, { width: 20 },
        { width: 12 }, { width: 12 }, { width: 15 }, { width: 15 },
        { width: 12 }, { width: 12 }, { width: 12 }
      ];
      XLSX.utils.book_append_sheet(workbook, rawDataSheet, '原始数据明细');

      // 7. 图表数据说明
      const chartDataInfo = [
        ['图表说明', '数据来源', '用途'],
        ['培训模式占比饼图', '培训模式统计表', '展示各培训模式的人数分布比例'],
        ['培训类别占比饼图', '培训类别统计表', '展示不同业务类型的培训分布'],
        ['各地区培训排名柱状图', '地区培训排名表', '对比各地区总培训人数和自主培训人数'],
        ['自主培训地区人数排名', '自主培训地区排名表', '展示自主培训在各地区的分布情况'],
        ['自主培训地区班级数排名', '自主培训地区排名表', '展示自主培训班级数在各地区的分布'],
        ['', '', ''],
        ['数据统计说明', '', ''],
        ['数据来源', '培训信息收集系统', ''],
        ['统计月份', selectedMonth, ''],
        ['报告生成时间', getCurrentBeijingTime(), ''],
        ['数据记录数', trainingData.length, ''],
        ['', '', ''],
        ['图表建议', '', ''],
        ['饼图', '适合查看占比分布', '推荐用于展示培训模式和类别占比'],
        ['柱状图', '适合查看排名对比', '推荐用于地区间培训数据对比分析'],
      ];
      const chartInfoSheet = XLSX.utils.aoa_to_sheet(chartDataInfo);
      chartInfoSheet['!cols'] = [{ width: 25 }, { width: 20 }, { width: 30 }];
      XLSX.utils.book_append_sheet(workbook, chartInfoSheet, '图表数据说明');

      // 生成文件名
      const fileName = `培训数据分析报告_${selectedMonth}_${getCurrentBeijingTime(TIME_FORMATS.FILE_TIMESTAMP)}.xlsx`;

      // 导出文件
      XLSX.writeFile(workbook, fileName);

      message.success('报告导出成功！包含7个工作表的详细分析数据，可在Excel中基于数据创建图表');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请重试');
    }
  };

  // 导出下个月计划数据表格
  const exportNextMonthPlanData = () => {
    const planData = trainingData.filter(item => item.next_month_classes > 0 || item.next_month_students > 0);

    if (planData.length === 0) {
      message.warning('暂无下个月计划数据可导出');
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      // 设置工作簿属性
      workbook.Props = {
        Title: `下个月培训计划数据表-${selectedMonth}`,
        Subject: "下个月培训计划数据统计",
        Author: "培训费用标准化检查与预警平台",
        CreatedDate: new Date()
      };

      // 准备表格数据
      const tableData = [
        [
          '序号',
          '地区',
          '业务类型',
          '培训模式',
          '联系人',
          '计划班级数',
          '计划学员数',
          '平均日薪',
          '预估总费用',
          '数据来源',
          '原始月份'
        ],
        ...planData.map((item: any, index: number) => {
          const estimatedCost = (item.next_month_students || 0) * (item.average_daily_salary || 0);
          return [
            index + 1,
            item.region_name || '',
            item.business_type_name || '',
            Array.isArray(item.training_modes) ? item.training_modes.join(', ') : item.training_modes,
            item.contact || '',
            item.next_month_classes || 0,
            item.next_month_students || 0,
            item.average_daily_salary || 0,
            estimatedCost,
            item.original_month ? '历史计划' : '本月计划',
            item.original_month || item.collection_month || ''
          ];
        })
      ];

      // 创建工作表
      const worksheet = XLSX.utils.aoa_to_sheet(tableData);

      // 设置列宽
      worksheet['!cols'] = [
        { width: 8 },   // 序号
        { width: 15 },  // 地区
        { width: 20 },  // 业务类型
        { width: 20 },  // 培训模式
        { width: 15 },  // 联系人
        { width: 12 },  // 计划班级数
        { width: 12 },  // 计划学员数
        { width: 12 },  // 平均日薪
        { width: 15 },  // 预估总费用
        { width: 12 },  // 数据来源
        { width: 15 }   // 原始月份
      ];

      // 添加工作表到工作簿
      XLSX.utils.book_append_sheet(workbook, worksheet, '下个月计划数据');

      // 添加汇总统计工作表
      const summaryData = [
        ['下个月培训计划数据汇总', ''],
        ['统计月份', selectedMonth],
        ['记录数量', planData.length],
        [''],
        ['计划班级总数', planData.reduce((sum, item) => sum + (item.next_month_classes || 0), 0)],
        ['计划学员总数', planData.reduce((sum, item) => sum + (item.next_month_students || 0), 0)],
        ['预估总费用', planData.reduce((sum, item) => sum + ((item.next_month_students || 0) * (item.average_daily_salary || 0)), 0)],
        [''],
        ['按地区统计', ''],
        ...(() => {
          const regionStats = new Map<string, { classes: number; students: number; cost: number }>();
          planData.forEach(item => {
            const region = item.region_name || '未知地区';
            const existing = regionStats.get(region) || { classes: 0, students: 0, cost: 0 };
            regionStats.set(region, {
              classes: existing.classes + (item.next_month_classes || 0),
              students: existing.students + (item.next_month_students || 0),
              cost: existing.cost + ((item.next_month_students || 0) * (item.average_daily_salary || 0))
            });
          });

          return Array.from(regionStats.entries())
            .map(([region, stats]) => [region, stats.classes, stats.students, stats.cost])
            .sort((a: any, b: any) => (b[1] as number) - (a[1] as number)); // 按班级数降序
        })(),
        [''],
        ['按业务类型统计', ''],
        ...(() => {
          const typeStats = new Map<string, { classes: number; students: number; cost: number }>();
          planData.forEach(item => {
            const type = item.business_type_name || '其他';
            const existing = typeStats.get(type) || { classes: 0, students: 0, cost: 0 };
            typeStats.set(type, {
              classes: existing.classes + (item.next_month_classes || 0),
              students: existing.students + (item.next_month_students || 0),
              cost: existing.cost + ((item.next_month_students || 0) * (item.average_daily_salary || 0))
            });
          });

          return Array.from(typeStats.entries())
            .map(([type, stats]) => [type, stats.classes, stats.students, stats.cost])
            .sort((a: any, b: any) => (b[1] as number) - (a[1] as number)); // 按班级数降序
        })()
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      summarySheet['!cols'] = [
        { width: 20 }, // 分类
        { width: 15 }, // 班级数
        { width: 15 }, // 学员数
        { width: 15 }  // 费用
      ];
      XLSX.utils.book_append_sheet(workbook, summarySheet, '数据汇总');

      // 生成文件名
      const fileName = `下个月培训计划数据_${selectedMonth}_${getCurrentBeijingTime(TIME_FORMATS.FILE_TIMESTAMP)}.xlsx`;

      // 导出文件
      XLSX.writeFile(workbook, fileName);

      message.success('下个月计划数据导出成功！包含详细数据和统计分析');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请重试');
    }
  };

  // 导出单张完整图片
  const exportAsFullImage = async () => {
    if (!hasData) {
      message.warning('暂无数据可导出');
      return;
    }

    try {
      message.loading('正在生成完整数据分析图片，请稍候...', 0);

      // 截取整个数据分析区域
      const analysisContainer = document.querySelector('.data-analysis-container') as HTMLElement;
      if (analysisContainer) {
        // 获取元素的实际尺寸
        const rect = analysisContainer.getBoundingClientRect();
        const actualHeight = analysisContainer.scrollHeight;
        const actualWidth = analysisContainer.scrollWidth;

        console.log('容器尺寸:', {
          width: actualWidth,
          height: actualHeight,
          rectHeight: rect.height,
          rectWidth: rect.width
        });

        // 临时调整容器样式以确保完整显示
        const originalOverflow = analysisContainer.style.overflow;
        const originalHeight = analysisContainer.style.height;
        analysisContainer.style.overflow = 'visible';
        analysisContainer.style.height = 'auto';

        // 等待样式应用
        await new Promise(resolve => setTimeout(resolve, 100));

        const canvas = await html2canvas(analysisContainer, {
          backgroundColor: '#ffffff',
          scale: 1.2, // 降低一点分辨率提高成功率
          useCORS: true,
          allowTaint: true,
          logging: true, // 开启日志以便调试
          width: actualWidth,
          height: actualHeight,
          scrollX: 0,
          scrollY: 0,
          x: 0,
          y: 0,
          // 确保捕获完整内容
          foreignObjectRendering: true
        });

        // 恢复原始样式
        analysisContainer.style.overflow = originalOverflow;
        analysisContainer.style.height = originalHeight;

        console.log('Canvas尺寸:', { width: canvas.width, height: canvas.height });

        // 转换为blob并下载
        canvas.toBlob((blob) => {
          if (blob) {
            const fileName = `培训数据分析完整报告_${selectedMonth}_${getCurrentBeijingTime(TIME_FORMATS.FILE_TIMESTAMP)}.png`;
            FileSaver(blob, fileName);
            message.destroy();
            message.success('完整数据分析图片导出成功！');
          } else {
            message.destroy();
            message.error('图片生成失败');
          }
        }, 'image/png', 0.95);
      } else {
        message.destroy();
        message.error('未找到数据分析区域');
      }
    } catch (error) {
      message.destroy();
      console.error('完整图片导出失败:', error);
      message.error('图片导出失败，请重试');
    }
  };

  // 备用方案：分段截图然后拼接
  const exportAsFullImageAdvanced = async () => {
    if (!hasData) {
      message.warning('暂无数据可导出');
      return;
    }

    try {
      message.loading('正在生成高质量完整图片，请稍候...', 0);

      const analysisContainer = document.querySelector('.data-analysis-container') as HTMLElement;
      if (!analysisContainer) {
        message.destroy();
        message.error('未找到数据分析区域');
        return;
      }

      // 先滚动到顶部
      window.scrollTo(0, 0);
      await new Promise(resolve => setTimeout(resolve, 300));

      // 获取所有卡片元素
      const cards = analysisContainer.querySelectorAll('.ant-card');
      const canvases: HTMLCanvasElement[] = [];

      // 逐个截图每个卡片
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i] as HTMLElement;

        // 滚动到卡片位置
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        await new Promise(resolve => setTimeout(resolve, 500));

        const canvas = await html2canvas(card, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false
        });

        canvases.push(canvas);
      }

      // 创建一个大画布来拼接所有卡片
      const totalWidth = Math.max(...canvases.map(c => c.width));
      const totalHeight = canvases.reduce((sum, c) => sum + c.height + 20, 0); // 添加间距

      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = totalWidth;
      finalCanvas.height = totalHeight;
      const ctx = finalCanvas.getContext('2d');

      if (ctx) {
        // 设置白色背景
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, totalWidth, totalHeight);

        // 拼接所有卡片
        let currentY = 0;
        canvases.forEach(canvas => {
          ctx.drawImage(canvas, 0, currentY);
          currentY += canvas.height + 20; // 添加间距
        });

        // 导出最终图片
        finalCanvas.toBlob((blob) => {
          if (blob) {
            const fileName = `培训数据分析完整报告_${selectedMonth}_${getCurrentBeijingTime(TIME_FORMATS.FILE_TIMESTAMP)}.png`;
            FileSaver(blob, fileName);
            message.destroy();
            message.success('高质量完整图片导出成功！');
          }
        }, 'image/png', 1.0);
      }

      // 滚动回顶部
      window.scrollTo(0, 0);

    } catch (error) {
      message.destroy();
      console.error('高级图片导出失败:', error);
      message.error('图片导出失败，请重试');
    }
  };

  // 导出多张图表图片
  const exportChartsAsImages = async () => {
    if (!hasData) {
      message.warning('暂无数据可导出');
      return;
    }

    try {
      message.loading('正在生成图表图片，请稍候...', 0);

      const zip = new JSZip();
      const chartFolder = zip.folder('培训数据分析图表');

      // 定义需要截图的图表元素
      const chartElements = [
        {
          selector: '.recharts-wrapper',
          name: '图表',
          containerSelector: '[data-chart="mode-pie"]'
        },
        {
          selector: '.recharts-wrapper',
          name: '培训类别占比图',
          containerSelector: '[data-chart="type-pie"]'
        },
        {
          selector: '.recharts-wrapper',
          name: '各地区培训排名图',
          containerSelector: '[data-chart="region-bar"]'
        },
        {
          selector: '.recharts-wrapper',
          name: '自主培训地区人数排名图',
          containerSelector: '[data-chart="self-region-people"]'
        },
        {
          selector: '.recharts-wrapper',
          name: '自主培训地区班级数排名图',
          containerSelector: '[data-chart="self-region-classes"]'
        }
      ];

      let chartIndex = 0;
      for (const chartInfo of chartElements) {
        try {
          const container = document.querySelector(chartInfo.containerSelector);
          if (container) {
            const chartElement = container.querySelector(chartInfo.selector) as HTMLElement;
            if (chartElement) {
              const canvas = await html2canvas(chartElement, {
                backgroundColor: '#ffffff',
                scale: 2, // 提高分辨率
                useCORS: true,
                allowTaint: true,
                logging: false,
                width: chartElement.offsetWidth,
                height: chartElement.offsetHeight
              });

              // 转换为blob
              const blob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((blob) => {
                  resolve(blob!);
                }, 'image/png', 0.9);
              });

              chartFolder?.file(`${String(chartIndex + 1).padStart(2, '0')}_${chartInfo.name}.png`, blob);
              chartIndex++;
            }
          }
        } catch (error) {
          console.warn(`截取图表 ${chartInfo.name} 失败:`, error);
        }
      }

      // 添加概览截图
      try {
        const overviewElement = document.querySelector('[data-chart="overview"]') as HTMLElement;
        if (overviewElement) {
          const canvas = await html2canvas(overviewElement, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false
          });

          const blob = await new Promise<Blob>((resolve) => {
            canvas.toBlob((blob) => {
              resolve(blob!);
            }, 'image/png', 0.9);
          });

          chartFolder?.file('00_数据概览.png', blob);
        }
      } catch (error) {
        console.warn('截取概览失败:', error);
      }

      // 添加说明文件
      const readmeContent = `培训数据分析图表导出
===============================

导出时间: ${getCurrentBeijingTime()}
统计月份: ${selectedMonth}
数据记录数: ${trainingData.length}

图表说明:
- 00_数据概览.png: 整体数据统计卡片
- 01_图表.png: 培训模式占比饼图
- 02_培训类别占比图.png: 培训类别分布饼图
- 03_各地区培训排名图.png: 地区培训对比柱状图
- 04_自主培训地区人数排名图.png: 自主培训地区人数分布
- 05_自主培训地区班级数排名图.png: 自主培训地区班级数分布

注意事项:
- 图片为PNG格式，分辨率为2倍原图
- 适合用于PPT演示或报告插图
- 建议配合Excel数据表格一起使用
`;

      chartFolder?.file('README.txt', readmeContent);

      // 生成ZIP文件
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const fileName = `培训数据分析图表_${selectedMonth}_${getCurrentBeijingTime(TIME_FORMATS.FILE_TIMESTAMP)}.zip`;

      // 下载文件
      FileSaver(zipBlob, fileName);

      message.destroy();
      message.success(`图表导出成功！共导出 ${chartIndex + 1} 张图片`);

    } catch (error) {
      message.destroy();
      console.error('图表导出失败:', error);
      message.error('图表导出失败，请重试');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 获取指定月份的培训信息数据
      const params: TrainingInfoQueryParams = {
        month: selectedMonth,
        page: 1,
        limit: 1000 // 获取所有数据用于统计
      };

      console.log('🔍 数据分析页面 - API调用参数:', { selectedMonth, params });

      const response = await getTrainingInfoList(params);
      let data = response.data || [];

      console.log('📊 数据分析页面 - API响应:', { responseKeys: Object.keys(response), dataLength: data.length, data });

      // 重置历史数据状态
      setIsShowingHistoricalData(false);

      // 如果当月没有数据，尝试获取上个月填写的下个月计划数据
      if (data.length === 0) {
        console.log('🔍 当月无数据，尝试查找上个月填写的计划数据...');

        // 计算上个月
        const selectedDate = dayjs(selectedMonth, 'YYYY-MM');
        const previousMonth = selectedDate.subtract(1, 'month').format('YYYY-MM');

        console.log('🗓️ 查询上个月:', previousMonth, '的下个月计划数据');

        // 查询上个月的数据，然后筛选出下个月等于选中月份的数据
        const previousParams: TrainingInfoQueryParams = {
          month: previousMonth,
          page: 1,
          limit: 1000
        };

        const previousResponse = await getTrainingInfoList(previousParams);
        const previousData = previousResponse.data || [];

        console.log('📊 上个月数据:', { previousDataLength: previousData.length, previousData });

        // 筛选出下个月计划数据
        const nextMonthPlanData = previousData.filter(item => {
          // 计算该记录的下个月应该是什么时候
          const itemDate = dayjs(item.collection_month, 'YYYY-MM');
          const expectedNextMonth = itemDate.add(1, 'month').format('YYYY-MM');
          return expectedNextMonth === selectedMonth &&
                 (item.next_month_students > 0 || item.next_month_classes > 0);
        });

        console.log('🎯 筛选后的下个月计划数据:', {
          filteredCount: nextMonthPlanData.length,
          filteredData: nextMonthPlanData
        });

        if (nextMonthPlanData.length > 0) {
          // 将下个月的计划数据转换为当月数据进行统计
          data = nextMonthPlanData.map(item => ({
            ...item,
            current_month_students: item.next_month_students,
            current_month_classes: item.next_month_classes,
            collection_month: selectedMonth, // 更新收集月份为当前选择的月份
            original_month: item.collection_month // 保留原始月份信息
          }));

          setIsShowingHistoricalData(true);
          message.info(`${selectedMonth} 月份暂无实际数据，已切换到分析 ${previousMonth} 填写的计划数据`);
        } else {
          setIsShowingHistoricalData(false);
        }
      }

      setTrainingData(data);
      const processedData = processTrainingData(data);
      setAnalysisData(processedData);

      console.log('📈 数据分析页面 - 处理后的数据:', processedData);

      if (data.length === 0) {
        message.info(`${selectedMonth} 月份暂无培训数据`);
      } else {
        message.success(`成功加载 ${data.length} 条培训数据`);
      }

    } catch (error: any) {
      console.error('获取数据失败:', error);
      setError(error.message || '获取数据失败，请重试');
      message.error('获取数据失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // 统计数据表格列定义
  const statisticsColumns = [
    {
      title: '培训模式',
      dataIndex: 'mode',
      key: 'mode',
    },
    {
      title: '培训人数',
      dataIndex: 'people',
      key: 'people',
      render: (value: number) => value.toLocaleString()
    },
    {
      title: '班级数',
      dataIndex: 'classes',
      key: 'classes',
      render: (value: number) => value || '--'
    }
  ];

  // 培训模式占比饼图数据
  const trainingModePieData = analysisData.trainingModeStats.map(item => ({
    name: item.mode,
    value: item.people
  }));

  // 培训类别饼图数据（基于业务类型）
  const trainingTypePieData = analysisData.trainingTypeStats.map(item => ({
    name: item.type,
    value: item.people
  }));

  // 地区培训排名柱状图数据
  const regionColumnData = analysisData.regionStats.slice(0, 10); // 只显示前10个地区

  // 自主培训地区排名柱状图数据
  const selfTrainingRegionData = analysisData.selfTrainingRegionStats.slice(0, 10);

  // 检查是否有数据
  const hasData = trainingData.length > 0;

  // 图表颜色配置
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // 自定义饼图标签渲染
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  // 自定义柱状图标签渲染
  const renderBarLabel = (props: any) => {
    const { x, y, width, value, dataKey } = props;

    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#666"
        textAnchor="middle"
        fontSize="12"
        dominantBaseline="auto"
      >
        {value.toLocaleString()}
      </text>
    );
  };

  return (
    <div style={{ padding: '0 24px' }} className="data-analysis-container">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>培训数据分析</Title>
        <Space>
          <DatePicker.MonthPicker
            value={dayjs(selectedMonth)}
            onChange={(date) => setSelectedMonth(date?.format('YYYY-MM') || getCurrentBeijingTime(TIME_FORMATS.MONTH))}
            placeholder="选择月份"
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            刷新数据
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportAnalysisReport}
            disabled={!hasData || loading}
          >
            导出Excel报告
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'normal',
                  icon: <PictureOutlined />,
                  label: '标准截图',
                  onClick: exportAsFullImage
                },
                {
                  key: 'advanced',
                  icon: <PictureOutlined />,
                  label: '高质量截图',
                  onClick: exportAsFullImageAdvanced
                }
              ]
            }}
            disabled={!hasData || loading}
          >
            <Button icon={<PictureOutlined />}>
              导出完整图片 <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            icon={<PictureOutlined />}
            onClick={exportChartsAsImages}
            disabled={!hasData || loading}
            style={{ marginLeft: 0 }}
          >
            导出分图表
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {/* 错误提示 */}
        {error && (
          <Alert
            message="数据加载失败"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 24 }}
          />
        )}

        {/* 概览统计卡片 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }} data-chart="overview">
          <Col span={6}>
            <Card>
              <Statistic
                title="总培训人数"
                value={analysisData.overview.totalPeople}
                suffix="人"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总班级数"
                value={analysisData.overview.totalClasses}
                suffix="个"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="自主培训人数"
                value={analysisData.overview.selfTrainingPeople}
                suffix="人"
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="合作培训人数"
                value={analysisData.overview.cooperativeTrainingPeople}
                suffix="人"
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 当月数据统计表格 */}
        <Card title={<><BarChartOutlined /> {selectedMonth} 培训数据统计</>} style={{ marginBottom: 24 }}>
          <Table
            dataSource={analysisData.trainingModeStats}
            columns={statisticsColumns}
            pagination={false}
            rowKey="mode"
            size="middle"
            locale={{ emptyText: '暂无数据' }}
          />
        </Card>

        {/* 图表展示区域 */}
        <Row gutter={[16, 16]}>
          {/* 各培训模式占比分析 */}
          <Col span={12}>
            <Card title={<><PieChartOutlined /> 各培训模式占比分析</>} data-chart="mode-pie">
              {hasData && trainingModePieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={trainingModePieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {trainingModePieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any) => [`${value}人`, '培训人数']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Empty description="暂无培训模式数据" />
                </div>
              )}
            </Card>
          </Col>

          {/* 培训类别占比 */}
          <Col span={12}>
            <Card title={<><PieChartOutlined /> 培训类别占比</>} data-chart="type-pie">
              {hasData && trainingTypePieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={trainingTypePieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {trainingTypePieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any) => [`${value}人`, '培训人数']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Empty description="暂无培训类别数据" />
                </div>
              )}
            </Card>
          </Col>

          {/* 各地区培训排名 */}
          <Col span={12}>
            <Card title={<><BarChartOutlined /> 各地区培训人数排名</>} data-chart="region-bar">
              {hasData && regionColumnData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={regionColumnData}
                    margin={{
                      top: 40,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="region" />
                    <YAxis />
                    <Tooltip formatter={(value: any) => [`${value}人`]} />
                    <Legend />
                    <Bar dataKey="total" fill="#8884d8" name="总培训人数" label={renderBarLabel} />
                    <Bar dataKey="self" fill="#82ca9d" name="自主培训人数" label={renderBarLabel} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Empty description="暂无地区数据" />
                </div>
              )}
            </Card>
          </Col>

          {/* 自主培训各地区人数排名 */}
          <Col span={12}>
            <Card title={<><BarChartOutlined /> 自主培训各地区人数排名</>} data-chart="self-region-people">
              {hasData && selfTrainingRegionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={selfTrainingRegionData}
                    margin={{
                      top: 40,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="region" />
                    <YAxis />
                    <Tooltip formatter={(value: any) => [`${value}人`, '自主培训人数']} />
                    <Legend />
                    <Bar dataKey="people" name="自主培训人数" label={renderBarLabel}>
                      {selfTrainingRegionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Empty description="暂无自主培训地区数据" />
                </div>
              )}
            </Card>
          </Col>

          {/* 自主培训各培训类别计划开班数 */}
          <Col span={12}>
            <Card title={<><BarChartOutlined /> 自主培训各培训类别计划开班数</>} data-chart="self-training-type-planned-classes">
              {hasData && analysisData.selfTrainingTypePlanStats && analysisData.selfTrainingTypePlanStats.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={analysisData.selfTrainingTypePlanStats || []}
                    margin={{
                      top: 40,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="type"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis />
                    <Tooltip formatter={(value: any) => [`${value}个`, '计划开班数']} />
                    <Legend />
                    <Bar dataKey="plannedClasses" fill="#ff7300" name="计划开班数" label={renderBarLabel}>
                      {analysisData.selfTrainingTypePlanStats?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Empty description="暂无自主培训计划开班数据" />
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* 下个月数据表格 - 只在当月无实际数据且有历史计划数据时显示 */}
        {isShowingHistoricalData && (
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card
                title={<><BarChartOutlined /> {selectedMonth} 培训计划数据表</>}
                extra={
                  <Space>
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      显示 {trainingData.filter(item => item.next_month_classes > 0 || item.next_month_students > 0).length} 条计划记录
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      icon={<ExportOutlined />}
                      onClick={exportNextMonthPlanData}
                    >
                      导出数据
                    </Button>
                  </Space>
                }
              >
                {trainingData.filter(item => item.next_month_classes > 0 || item.next_month_students > 0).length > 0 ? (
                  <Table
                  dataSource={trainingData
                    .filter(item => item.next_month_classes > 0 || item.next_month_students > 0)
                    .map((item, index) => ({
                      ...item,
                      key: index,
                      plannedClasses: item.next_month_classes || 0,
                      plannedStudents: item.next_month_students || 0,
                      estimatedDailySalary: item.average_daily_salary || 0,
                    }))}
                  columns={[
                    {
                      title: '地区',
                      dataIndex: 'region_name',
                      key: 'region_name',
                      width: 120,
                      fixed: 'left'
                    },
                    {
                      title: '业务类型',
                      dataIndex: 'business_type_name',
                      key: 'business_type_name',
                      width: 150
                    },
                    {
                      title: '培训模式',
                      dataIndex: 'training_modes',
                      key: 'training_modes',
                      width: 120,
                      render: (modes: string[]) => (
                        <span>
                          {Array.isArray(modes) ? modes.join(', ') : modes}
                        </span>
                      )
                    },
                    {
                      title: '联系人',
                      dataIndex: 'contact',
                      key: 'contact',
                      width: 100
                    },
                    {
                      title: '计划班级数',
                      dataIndex: 'plannedClasses',
                      key: 'plannedClasses',
                      width: 100,
                      render: (value: number) => (
                        <span style={{ color: value > 0 ? '#1890ff' : '#999', fontWeight: value > 0 ? 'bold' : 'normal' }}>
                          {value} 个
                        </span>
                      ),
                      sorter: (a, b) => a.plannedClasses - b.plannedClasses
                    },
                    {
                      title: '计划学员数',
                      dataIndex: 'plannedStudents',
                      key: 'plannedStudents',
                      width: 120,
                      render: (value: number) => (
                        <span style={{ color: value > 0 ? '#52c41a' : '#999', fontWeight: value > 0 ? 'bold' : 'normal' }}>
                          {value.toLocaleString()} 人
                        </span>
                      ),
                      sorter: (a, b) => a.plannedStudents - b.plannedStudents
                    },
                    {
                      title: '平均日薪',
                      dataIndex: 'estimatedDailySalary',
                      key: 'estimatedDailySalary',
                      width: 120,
                      render: (value: number) => (
                        <span style={{ color: '#faad14' }}>
                          ¥{value.toLocaleString()}
                        </span>
                      ),
                      sorter: (a, b) => a.estimatedDailySalary - b.estimatedDailySalary
                    },
                    {
                      title: '预估总费用',
                      key: 'estimatedCost',
                      width: 120,
                      render: (record: any) => {
                        const totalCost = (record.plannedStudents || 0) * (record.estimatedDailySalary || 0);
                        return (
                          <span style={{ color: '#f5222d', fontWeight: 'bold' }}>
                            ¥{totalCost.toLocaleString()}
                          </span>
                        );
                      },
                      sorter: (a, b) => {
                        const costA = (a.plannedStudents || 0) * (a.estimatedDailySalary || 0);
                        const costB = (b.plannedStudents || 0) * (b.estimatedDailySalary || 0);
                        return costA - costB;
                      }
                    },
                    {
                      title: '数据来源',
                      key: 'dataSource',
                      width: 100,
                      render: (record: any) => (
                        <Tag color={record.original_month ? 'orange' : 'blue'}>
                          {record.original_month ? '历史计划' : '本月计划'}
                        </Tag>
                      )
                    }
                  ]}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `第 ${range[0]}-${range[1]} 条，共 ${total} 条计划记录`
                  }}
                  scroll={{ x: 1000 }}
                  size="middle"
                  style={{ marginTop: 16 }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Empty
                    description={
                      <div>
                        <p>暂无下个月培训计划数据</p>
                        <p style={{ fontSize: '12px', color: '#999' }}>
                          当前筛选条件下没有找到计划开班或招生记录
                        </p>
                      </div>
                    }
                  />
                </div>
                )}
              </Card>
            </Col>
          </Row>
        )}
      </Spin>
    </div>
  );
};

export default TrainingDataAnalysis;